import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI, orgAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ACTIVE_ORG_KEY = 'activeOrgId';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeOrgId, setActiveOrgIdState] = useState(() => localStorage.getItem(ACTIVE_ORG_KEY) || null);

  const setActiveOrgId = useCallback((orgId) => {
    setActiveOrgIdState(orgId);
    if (orgId) {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    } else {
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
  }, []);

  const refreshOrganizations = useCallback(async () => {
    try {
      const r = await orgAPI.listMine();
      const orgs = r.data || [];
      setOrganizations(orgs);
      // Revalidate the active org: if it no longer exists in the membership
      // list (member removed mid-session), clear it so the picker re-engages.
      setActiveOrgIdState((current) => {
        if (current && !orgs.some((o) => o.id === current)) {
          localStorage.removeItem(ACTIVE_ORG_KEY);
          return null;
        }
        return current;
      });
      return orgs;
    } catch (_err) {
      setOrganizations([]);
      return [];
    }
  }, []);

  const persistSession = async (token) => {
    localStorage.setItem('token', token);
    const userResp = await authAPI.getCurrentUser();
    const userData = userResp.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    const orgs = await refreshOrganizations();
    // Slack-style: exactly one org → auto-select it. Two+ → keep a stored
    // selection if still valid, otherwise the org picker decides.
    const stored = localStorage.getItem(ACTIVE_ORG_KEY);
    const stillValid = stored && orgs.some((o) => o.id === stored);
    const next = orgs.length === 1 ? orgs[0].id : stillValid ? stored : null;
    setActiveOrgId(next);
    return userData;
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
          const orgs = await refreshOrganizations();
          const stored = localStorage.getItem(ACTIVE_ORG_KEY);
          const stillValid = stored && orgs.some((o) => o.id === stored);
          const next = orgs.length === 1 ? orgs[0].id : stillValid ? stored : null;
          setActiveOrgId(next);
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem(ACTIVE_ORG_KEY);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [refreshOrganizations]);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await authAPI.login(username, password);
      const { access_token, is_temporary_password } = response.data;
      const userData = await persistSession(access_token);
      return {
        success: true,
        is_temporary_password: is_temporary_password || userData.is_temporary_password,
      };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogleToken = async (idToken, inviteToken) => {
    try {
      setError(null);
      const resp = await authAPI.googleTokenLogin(idToken, inviteToken);
      const { access_token } = resp.data;
      await persistSession(access_token);
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Google login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithDevEmail = async (email, fullName, inviteToken) => {
    try {
      setError(null);
      const resp = await authAPI.devLogin(email, fullName, inviteToken);
      const { access_token } = resp.data;
      await persistSession(access_token);
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Dev login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithToken = async (token) => {
    try {
      setError(null);
      await persistSession(token);
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Token login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const acceptInvite = async (token) => {
    try {
      setError(null);
      const resp = await authAPI.acceptInvite(token);
      await refreshOrganizations();
      return { success: true, org: resp.data };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Could not accept invitation';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(ACTIVE_ORG_KEY);
    setUser(null);
    setOrganizations([]);
    setActiveOrgIdState(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const isAdmin = () => user?.role === 'admin';

  // Org scoping helpers — based on the ACTIVE workspace, Slack-style.
  const activeOrg = organizations.find((o) => o.id === activeOrgId) || null;
  const activeOrgRole = activeOrg?.my_role || null;

  const isOrgOwner = () => {
    if (!activeOrg) return false;
    return activeOrg.my_role === 'owner' || activeOrg.my_role === 'admin';
  };

  const isOrgMember = () => !!activeOrg;

  const value = {
    user,
    organizations,
    loading,
    error,
    activeOrgId,
    activeOrg,
    activeOrgRole,
    setActiveOrgId,
    login,
    loginWithGoogleToken,
    loginWithDevEmail,
    loginWithToken,
    acceptInvite,
    logout,
    updateUser,
    refreshOrganizations,
    isAdmin,
    isOrgOwner,
    isOrgMember,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
